import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ShieldAlert,
  Lock,
  LogOut,
  RefreshCw,
  Users,
  UserCheck,
  Youtube,
  Instagram,
  FileText,
  Video,
  Download,
  Search,
  Trash2,
  CheckCircle2,
  Sparkles,
  ExternalLink,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/Logo";
import { useConfirm } from "@/context/ConfirmContext";

const ADMIN_TOKEN_KEY = "virality_admin_key";

export default function Admin() {
  const { confirm } = useConfirm();
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(ADMIN_TOKEN_KEY) || "");
  const [inputPassword, setInputPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [users, setUsers] = useState([]);
  const [connections, setConnections] = useState([]);
  const [contentItems, setContentItems] = useState([]);

  const [waitlistSearch, setWaitlistSearch] = useState("");
  const [waitlistStatusFilter, setWaitlistStatusFilter] = useState("all");

  const authHeaders = useCallback(() => {
    return { "X-Admin-Key": adminKey };
  }, [adminKey]);

  const loadData = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const headers = { "X-Admin-Key": adminKey };
      const [sumRes, waitRes, usersRes, connRes, contRes] = await Promise.all([
        api.get("/admin/summary", { headers }).catch(() => null),
        api.get("/admin/waitlist", { headers }).catch(() => ({ data: { waitlist: [] } })),
        api.get("/admin/users", { headers }).catch(() => ({ data: { users: [] } })),
        api.get("/admin/connections", { headers }).catch(() => ({ data: { connections: [] } })),
        api.get("/admin/content", { headers }).catch(() => ({ data: { content: [] } })),
      ]);

      if (sumRes?.data) setSummary(sumRes.data);
      if (waitRes?.data?.waitlist) setWaitlist(waitRes.data.waitlist);
      if (usersRes?.data?.users) setUsers(usersRes.data.users);
      if (connRes?.data?.connections) setConnections(connRes.data.connections);
      if (contRes?.data?.content) setContentItems(contRes.data.content);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired or invalid admin password.");
        sessionStorage.removeItem(ADMIN_TOKEN_KEY);
        setAdminKey("");
      } else {
        toast.error(apiErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => {
    if (adminKey) {
      loadData();
    }
  }, [adminKey, loadData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!inputPassword) return;
    setLoggingIn(true);
    try {
      const { data } = await api.post("/admin/auth", { password: inputPassword });
      sessionStorage.setItem(ADMIN_TOKEN_KEY, data.token || inputPassword);
      setAdminKey(data.token || inputPassword);
      toast.success("Admin authenticated successfully.");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    setAdminKey("");
    setSummary(null);
    setWaitlist([]);
    setUsers([]);
    setConnections([]);
    toast.success("Logged out from admin portal.");
  };

  const handleUpdateWaitlistStatus = async (id, status) => {
    try {
      await api.put(`/admin/waitlist/${id}`, { status }, { headers: authHeaders() });
      setWaitlist((prev) =>
        prev.map((w) => (w.id === id ? { ...w, status } : w))
      );
      toast.success(`Updated status to ${status}`);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const handleDeleteWaitlist = async (id) => {
    const ok = await confirm({
      title: "Delete waitlist submission?",
      description: "This waitlist entry will be permanently deleted.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/waitlist/${id}`, { headers: authHeaders() });
      setWaitlist((prev) => prev.filter((w) => w.id !== id));
      toast.success("Waitlist entry removed.");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const exportWaitlistCSV = () => {
    if (waitlist.length === 0) {
      toast.error("No waitlist entries to export.");
      return;
    }
    const headers = ["Position", "Email", "Niche", "Platforms", "Status", "Notes", "Created At"];
    const rows = waitlist.map((w) => [
      w.position || "",
      `"${w.email || ""}"`,
      `"${w.niche || ""}"`,
      `"${(w.platforms || []).join(", ")}"`,
      `"${w.status || "pending"}"`,
      `"${(w.notes || "").replace(/"/g, '""')}"`,
      `"${w.created_at || ""}"`,
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `virality-waitlist-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Exported waitlist CSV.");
  };

  const filteredWaitlist = waitlist.filter((w) => {
    if (waitlistStatusFilter !== "all" && w.status !== waitlistStatusFilter) return false;
    if (waitlistSearch) {
      const q = waitlistSearch.toLowerCase();
      const emailMatch = (w.email || "").toLowerCase().includes(q);
      const nicheMatch = (w.niche || "").toLowerCase().includes(q);
      const notesMatch = (w.notes || "").toLowerCase().includes(q);
      if (!emailMatch && !nicheMatch && !notesMatch) return false;
    }
    return true;
  });

  if (!adminKey) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-8 shadow-2xl">
          <div className="text-center space-y-2">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent/15 text-accent">
              <Lock className="h-6 w-6" />
            </div>
            <h1 className="font-display text-2xl font-bold text-foreground">Admin Portal</h1>
            <p className="text-xs text-muted-foreground">
              Please enter the administrator access key to view website data.
            </p>
          </div>

          <form onSubmit={handleLogin} className="mt-6 space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-foreground mb-1.5">
                Admin Password
              </label>
              <Input
                type="password"
                required
                value={inputPassword}
                onChange={(e) => setInputPassword(e.target.value)}
                placeholder="Enter password..."
                className="bg-background text-sm"
                autoFocus
                data-testid="admin-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loggingIn}
              className="w-full bg-accent font-bold text-accent-foreground hover:bg-accent-hover"
              data-testid="admin-login-button"
            >
              {loggingIn ? "Verifying key..." : "Unlock admin dashboard"}
            </Button>
          </form>

          <div className="mt-6 border-t border-border/50 pt-4 text-center">
            <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">
              &larr; Back to main site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const stats = summary?.stats || {};

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top Header */}
      <header className="sticky top-0 z-50 border-b border-border/80 bg-surface/90 backdrop-blur-md px-4 sm:px-6 py-3 sm:py-3.5">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <Logo />
            <span className="rounded-full bg-accent/15 px-2 sm:px-2.5 py-0.5 text-[9px] sm:text-[10px] font-extrabold uppercase tracking-wider text-accent border border-accent/30">
              Admin
            </span>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              size="sm"
              variant="outline"
              onClick={loadData}
              disabled={loading}
              className="h-8 text-xs gap-1.5 px-2.5 sm:px-3"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleLogout}
              className="h-8 text-xs text-muted-foreground hover:text-destructive gap-1.5 px-2.5 sm:px-3"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-8">
        {/* Navigation Tabs */}
        <div className="flex items-center gap-2 border-b border-border pb-3 mb-6 sm:mb-8 overflow-x-auto scrollbar-hide">
          {[
            { id: "overview", label: "Overview", icon: Users },
            { id: "waitlist", label: `Waitlist (${waitlist.length})`, icon: UserCheck },
            { id: "users", label: `Users (${users.length})`, icon: ShieldAlert },
            { id: "connections", label: `Connections (${connections.length})`, icon: Youtube },
            { id: "content", label: `Content (${contentItems.length})`, icon: FileText },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex shrink-0 items-center gap-1.5 sm:gap-2 rounded-lg px-3 sm:px-4 py-2 text-xs font-semibold transition-all ${
                  active
                    ? "bg-accent text-accent-foreground shadow-sm"
                    : "border border-border bg-surface text-muted-foreground hover:bg-elevated hover:text-foreground"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* 1. OVERVIEW TAB */}
        {activeTab === "overview" && (
          <div className="space-y-6 sm:space-y-8">
            <div className="grid gap-3 sm:gap-4 grid-cols-1 xs:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl border border-border bg-surface p-4 sm:p-5 shadow-sm">
                <div className="flex items-center justify-between text-muted-foreground">
                  <span className="text-xs font-semibold uppercase tracking-wider">Waitlist Signups</span>
                  <UserCheck className="h-4 w-4 text-accent" />
                </div>
                <div className="mt-3 text-2xl sm:text-3xl font-extrabold text-foreground">{stats.total_waitlist ?? 0}</div>
                <p className="mt-1 text-[11px] text-muted-foreground">Creators awaiting early access</p>
              </div>

              <div className="rounded-xl border border-border bg-surface p-4 sm:p-5 shadow-sm">
                <div className="flex items-center justify-between text-muted-foreground">
                  <span className="text-xs font-semibold uppercase tracking-wider">Registered Users</span>
                  <Users className="h-4 w-4 text-blue-400" />
                </div>
                <div className="mt-3 text-2xl sm:text-3xl font-extrabold text-foreground">{stats.total_users ?? 0}</div>
                <p className="mt-1 text-[11px] text-muted-foreground">Active database accounts</p>
              </div>

              <div className="rounded-xl border border-border bg-surface p-4 sm:p-5 shadow-sm">
                <div className="flex items-center justify-between text-muted-foreground">
                  <span className="text-xs font-semibold uppercase tracking-wider">YouTube Channels</span>
                  <Youtube className="h-4 w-4 text-red-500" />
                </div>
                <div className="mt-3 text-2xl sm:text-3xl font-extrabold text-foreground">{stats.youtube_connections ?? 0}</div>
                <p className="mt-1 text-[11px] text-muted-foreground">OAuth synced channels</p>
              </div>

              <div className="rounded-xl border border-border bg-surface p-4 sm:p-5 shadow-sm">
                <div className="flex items-center justify-between text-muted-foreground">
                  <span className="text-xs font-semibold uppercase tracking-wider">Instagram Creators</span>
                  <Instagram className="h-4 w-4 text-pink-500" />
                </div>
                <div className="mt-3 text-2xl sm:text-3xl font-extrabold text-foreground">{stats.instagram_connections ?? 0}</div>
                <p className="mt-1 text-[11px] text-muted-foreground">Meta Graph synced accounts</p>
              </div>
            </div>

            <div className="grid gap-3 sm:gap-4 grid-cols-1 xs:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl border border-border bg-surface p-4 sm:p-5">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Content Drafts</span>
                <div className="mt-2 text-xl sm:text-2xl font-bold text-foreground">{stats.total_content_drafts ?? 0}</div>
              </div>
              <div className="rounded-xl border border-border bg-surface p-4 sm:p-5">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Production Projects</span>
                <div className="mt-2 text-xl sm:text-2xl font-bold text-foreground">{stats.total_production_projects ?? 0}</div>
              </div>
              <div className="rounded-xl border border-border bg-surface p-5">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Opportunities Generated</span>
                <div className="mt-2 text-2xl font-bold text-foreground">{stats.total_opportunities ?? 0}</div>
              </div>
              <div className="rounded-xl border border-border bg-surface p-5">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Intelligence Scans</span>
                <div className="mt-2 text-2xl font-bold text-foreground">{stats.total_intelligence_snapshots ?? 0}</div>
              </div>
            </div>

            {/* Recent Waitlist Submissions */}
            <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-base font-bold text-foreground">Recent Waitlist Signups</h3>
                <Button size="sm" variant="outline" onClick={() => setActiveTab("waitlist")} className="h-7 text-xs">
                  View all ({waitlist.length})
                </Button>
              </div>

              {waitlist.slice(0, 5).length === 0 ? (
                <p className="text-xs text-muted-foreground">No waitlist signups recorded yet.</p>
              ) : (
                <div className="divide-y divide-border">
                  {waitlist.slice(0, 5).map((w) => (
                    <div key={w.id} className="py-3 flex items-center justify-between gap-4 text-xs">
                      <div>
                        <span className="font-semibold text-foreground">{w.email}</span>
                        <p className="text-muted-foreground mt-0.5">
                          {w.niche || "No niche specified"} &middot; {(w.platforms || []).join(", ") || "All platforms"}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="rounded bg-accent/10 text-accent font-semibold px-2 py-0.5 text-[10px] uppercase">
                          Spot #{w.position || 1}
                        </span>
                        <p className="text-muted-foreground text-[10px] mt-1">
                          {w.created_at ? new Date(w.created_at).toLocaleString() : ""}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 2. WAITLIST TAB */}
        {activeTab === "waitlist" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    value={waitlistSearch}
                    onChange={(e) => setWaitlistSearch(e.target.value)}
                    placeholder="Search by email or niche..."
                    className="pl-9 h-9 text-xs bg-surface"
                  />
                </div>
                <select
                  value={waitlistStatusFilter}
                  onChange={(e) => setWaitlistStatusFilter(e.target.value)}
                  className="rounded-md border border-border bg-surface px-3 py-2 text-xs text-foreground"
                >
                  <option value="all">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="invited">Invited</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <Button size="sm" variant="outline" onClick={exportWaitlistCSV} className="h-9 text-xs gap-1.5">
                <Download className="h-3.5 w-3.5" />
                <span>Export CSV</span>
              </Button>
            </div>

            <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-sm">
              <table className="w-full text-left text-xs">
                <thead className="border-b border-border bg-surface/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">#</th>
                    <th className="px-4 py-3">Creator Email</th>
                    <th className="px-4 py-3">Niche</th>
                    <th className="px-4 py-3">Platforms</th>
                    <th className="px-4 py-3">Bottleneck / Notes</th>
                    <th className="px-4 py-3">Joined Date</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredWaitlist.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                        No waitlist submissions found matching filters.
                      </td>
                    </tr>
                  ) : (
                    filteredWaitlist.map((w, idx) => (
                      <tr key={w.id} className="hover:bg-elevated/40 transition-colors">
                        <td className="px-4 py-3 font-mono font-bold text-accent">#{w.position || idx + 1}</td>
                        <td className="px-4 py-3 font-semibold text-foreground">{w.email}</td>
                        <td className="px-4 py-3 text-muted-foreground">{w.niche || "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {(w.platforms || []).length > 0 ? (w.platforms || []).join(", ") : "—"}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground max-w-xs truncate" title={w.notes}>
                          {w.notes || "—"}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                          {w.created_at ? new Date(w.created_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={w.status || "pending"}
                            onChange={(e) => handleUpdateWaitlistStatus(w.id, e.target.value)}
                            className="rounded border border-border bg-background px-2 py-1 text-[11px] text-foreground focus:outline-none"
                          >
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="invited">Invited</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteWaitlist(w.id)}
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                            title="Delete entry"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 3. REGISTERED USERS TAB */}
        {activeTab === "users" && (
          <div className="rounded-xl border border-border bg-surface shadow-sm overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-border bg-surface/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">User Email</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Connected Channels</th>
                  <th className="px-4 py-3">Content Count</th>
                  <th className="px-4 py-3">Joined Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No registered users in database.
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id} className="hover:bg-elevated/40 transition-colors">
                      <td className="px-4 py-3 font-semibold text-foreground">{u.email}</td>
                      <td className="px-4 py-3 text-muted-foreground">{u.name || "Creator"}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${
                          u.role === "admin" ? "bg-accent/20 text-accent" : "bg-elevated text-muted-foreground"
                        }`}>
                          {u.role || "user"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {(u.connections || []).length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {(u.connections || []).map((c, i) => (
                              <span key={i} className="rounded bg-elevated px-1.5 py-0.5 text-[10px] font-medium text-foreground">
                                {c.platform}: {c.account_name || "Connected"}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground/60">None connected</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-semibold text-foreground">{u.content_count ?? 0}</td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* 4. SOCIAL CONNECTIONS TAB */}
        {activeTab === "connections" && (
          <div className="rounded-xl border border-border bg-surface shadow-sm overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-border bg-surface/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Platform</th>
                  <th className="px-4 py-3">Channel / Handle</th>
                  <th className="px-4 py-3">Account ID</th>
                  <th className="px-4 py-3">User Email</th>
                  <th className="px-4 py-3">Connected At</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {connections.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No social accounts connected yet.
                    </td>
                  </tr>
                ) : (
                  connections.map((c) => (
                    <tr key={c.id} className="hover:bg-elevated/40 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 font-bold capitalize text-foreground">
                          {c.platform === "youtube" ? (
                            <Youtube className="h-4 w-4 text-red-500" />
                          ) : (
                            <Instagram className="h-4 w-4 text-pink-500" />
                          )}
                          <span>{c.platform}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-semibold text-foreground">
                        {c.account_name || c.channel_title || c.username || "Connected Account"}
                      </td>
                      <td className="px-4 py-3 font-mono text-muted-foreground text-[11px]">
                        {c.account_id || c.channel_id || "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{c.user_email || "Unknown"}</td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {c.connected_at ? new Date(c.connected_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded bg-emerald-500/15 text-emerald-400 font-bold px-2 py-0.5 text-[10px] uppercase">
                          Active Sync
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* 5. CONTENT AND PROJECTS TAB */}
        {activeTab === "content" && (
          <div className="rounded-xl border border-border bg-surface shadow-sm overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-border bg-surface/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Creator</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Production Status</th>
                  <th className="px-4 py-3">Platforms</th>
                  <th className="px-4 py-3">Created Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {contentItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No content items found.
                    </td>
                  </tr>
                ) : (
                  contentItems.map((item) => (
                    <tr key={item.id} className="hover:bg-elevated/40 transition-colors">
                      <td className="px-4 py-3 font-semibold text-foreground max-w-sm truncate">
                        {item.title || "Untitled draft"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{item.user_email || "—"}</td>
                      <td className="px-4 py-3">
                        <span className="rounded bg-elevated px-2 py-0.5 text-[10px] uppercase font-bold text-muted-foreground">
                          {item.status || "draft"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {item.production_status || "backlog"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {(item.platforms || []).join(", ") || "None"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {item.created_at ? new Date(item.created_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
